import { Languages } from '@wicadu/langscope'
import { PagesId } from '@ts-enums/PagesId'
import { ContentType } from '@ts-types/ArticleSection'
import { type Translations } from '@ts-types/Translations'
import { getUrlByPageId } from '@utils/getUrlByPageId'

const globalTranslations: Translations = {
  es: {
    seo_title: 'Subir y gestionar archivos',
    seo_description: 'Aprende cómo subir, actualizar o eliminar archivos como facturas o garantías en pedidos de wicadu. Guía paso a paso para optimizar la gestión de archivos con el Asistente de wicadu',
    seo_keywords: 'adjuntar documentos wicadu, gestionar documentos, actualizar documentos wicadu, eliminar documentos wicadu, gestión tienda wicadu, cómo adjuntar documentos en wicadu, pasos para actualizar documentos wicadu, cómo eliminar archivos en wicadu, subir documentos',
    page_id: PagesId.ManagingDocsForOrders,
    breadcrumbs: {
      home: 'Inicio',
      orders_home: 'Pedidos',
      how_to_manage_docs: 'Subir y gestionar archivos',
    },
    article: {
      title: 'Subir y gestionar archivos',
      intended: [
        `<strong>Propietarios</strong> o <strong>Administradores</strong> con acceso completo para gestionar <em>pedidos</em> y <em>archivos</em> en el <a href=${process.env.STORES_WEBAPP_HOST}>Asistente de wicadu</a>.`,
        `<strong>Usuarios</strong> con permisos para subir o modificar <em>adjuntos</em> en pedidos.`
      ],
      about: {
        title: 'Acerca de gestionar archivos',
        content_type: ContentType.Paragraph,
        content: [
          `Adjuntar archivos a <em>pedidos</em> en <strong>wicadu</strong> te permite centralizar archivos importantes, como <em>facturas</em>, <em>especificaciones de productos</em>, <em>garantías</em> o <em>documentos de identificación</em>, directamente en el <strong>Asistente de wicadu</strong> para una <strong>gestión eficiente de pedidos</strong>. Esta funcionalidad simplifica la comunicación con clientes y empleados, manteniendo toda la información relacionada con el pedido organizada y accesible.`
        ],
      },
      before_begin: {
        title: 'Antes de empezar',
        content_type: ContentType.Fragment,
        content: [
          {
            content_type: ContentType.Paragraph,
            content: [
              'Para adjuntar o gestionar archivos en pedidos de <strong>wicadu</strong>, asegúrate de lo siguiente:'
            ],
          },
          {
            content_type: ContentType.UnorderedList,
            content: [
              {
                content_type: ContentType.Paragraph,
                content: [
                  `Tener acceso a la sección <em>Pedidos</em>`
                ],
              },
              {
                content_type: ContentType.Paragraph,
                content: [
                  'Los archivos tienen un tamaño inferior a 2MB.'
                ],
              },
              {
                content_type: ContentType.Paragraph,
                content: [
                  `Los archivos están en formatos admitidos: <em>.jpg</em>, <em>.jpeg</em>, <em>.png</em>, <em>.svg</em>, <em>.webp</em> o <em>.pdf</em>.`
                ],
              },
              {
                content_type: ContentType.Paragraph,
                content: [
                  'No hay límite en la cantidad de archivos que puedes adjuntar a un pedido.'
                ],
              },

            ],
          },
          // ARTICLE IDEA
          // {
          //   content_type: ContentType.Paragraph,
          //   class: 'msg-alert',
          //   content: [
          //     `Consulta la <a href="http://help.localhost/es/orders/document-formats" title="Guía de formatos de archivos admitidos en wicadu">Guía de formatos de archivos admitidos</a> para más detalles sobre cómo gestionar <em>adjuntos</em> en todos los tipos de pedidos.`
          //   ],
          // },
        ],
      },
      how_to_add: {
        title: 'Subir un archivo',
        content_type: ContentType.Fragment,
        content: [
          {
            content_type: ContentType.Paragraph,
            content: [
              'Agregar archivos a tus <em>pedidos</em> en <strong>wicadu</strong> te ayuda a mantener toda la información importante en un solo lugar: desde <em>facturas</em> hasta <em>garantías</em> o comprobantes. Esto mejora la trazabilidad, reduce errores y facilita la gestión diaria.',
              'Sigue los pasos a continuación para subir un archivo: '
            ],
          },
          {
            content_type: ContentType.OrderedList,
            content: [
              {
                content_type: ContentType.ListItem,
                content: [
                  {
                    content_type: ContentType.Title3,
                    class: 'bold',
                    content: ['Selecciona el pedido en el módulo correspondiente: '],
                  },
                  {
                    content_type: ContentType.Paragraph,
                    content: [
                      'Dirígete a la funcionalidad de <em>Ventas</em> o <em>Compras</em> en el <strong>Asistente de wicadu</strong>, según el tipo de pedido, y selecciona el pedido desde las secciones <em>Preparado</em> o <em>Entregado</em>'
                    ],
                  },
                ],
              },
              {
                content_type: ContentType.ListItem,
                content: [
                  {
                    content_type: ContentType.Title3,
                    class: 'bold',
                    content: ['Carga los archivos: '],
                  },
                  {
                    content_type: ContentType.Paragraph,
                    content: [
                      'En la sección <em>Documentos</em>, elige una de estas opciones:'
                    ],
                  },
                  {
                    content_type: ContentType.UnorderedList,
                    content: [
                      {
                        content_type: ContentType.Paragraph,
                        content: [
                          '<b>Acceso rápido:</b> Haz clic en <strong>Subir Archivo</strong> para abrir la ventana del sistema operativo, selecciona el archivo y confirma. El campo de nombre se completará automáticamente, pero puedes modificarlo'
                        ],
                      },
                      {
                        content_type: ContentType.Paragraph,
                        content: [
                          '<b>Formulario completo</b>: Haz clic en <b>Editar</b>, completa los campos de nombre y archivo en el formulario desplegado y confirma.',
                        ],
                      },
                    ],
                  },
                ],
              },
              {
                content_type: ContentType.ListItem,
                content: [
                  {
                    content_type: ContentType.Title3,
                    class: 'bold',
                    content: ['Confirma la carga del archivo: '],
                  },
                  {
                    content_type: ContentType.Paragraph,
                    content: [
                      'Haz clic en <em>Guardar</em> para cargar el archivo. Los archivos aparecerán en una lista una vez cargados.'
                    ],
                  },
                ],
              },

            ],
          }
        ]
      },
      how_to_update: {
        title: 'Actualizar un archivo',
        content_type: ContentType.Fragment,
        content: [
          {
            content_type: ContentType.Paragraph,
            content: [
              'Actualizar documentos en <strong>wicadu</strong> garantiza que siempre cuentes con información precisa y vigente en cada pedido. Esta funcionalidad no solo fortalece la confianza de tus clientes, sino que también agiliza la gestión de órdenes, mejora la colaboración interna y optimiza la eficiencia operativa de tu tienda.',
              'Sigue los pasos a continuación para actualizar un archivo: '
            ],
          },
          {
            content_type: ContentType.OrderedList,
            content: [
              {
                content_type: ContentType.ListItem,
                content: [
                  {
                    content_type: ContentType.Title3,
                    class: 'bold',
                    content: ['Selecciona el pedido en el módulo correspondiente: '],
                  },
                  {
                    content_type: ContentType.Paragraph,
                    content: [
                      'Dirígete a la funcionalidad de <em>Ventas</em> o <em>Compras</em> en el <strong>Asistente de wicadu</strong>, según el tipo de pedido, y selecciona el pedido desde las secciones <em>Preparado</em> o <em>Entregado</em>'
                    ],
                  },
                ],
              },
              {
                content_type: ContentType.ListItem,
                content: [
                  {
                    content_type: ContentType.Title3,
                    class: 'bold',
                    content: ['Selecciona el archivo a actualizar: '],
                  },
                  {
                    content_type: ContentType.Paragraph,
                    content: [
                      'En la sección <em>Documentos</em> del pedido, haz clic en <strong>Editar</strong> para activar el <em>modo edición</em> y gestionar los archivos adjuntos disponibles.'
                    ],
                  },
                ],
              },
              {
                content_type: ContentType.ListItem,
                content: [
                  {
                    content_type: ContentType.Title3,
                    class: 'bold',
                    content: ['Habilita el formulario de actualización: '],
                  },
                  {
                    content_type: ContentType.Paragraph,
                    content: [
                      'Haz clic en el ícono de lápiz al lado izquierdo del archivo que deseas actualizar para desplegar el formulario, donde puedes modificar el nombre o subir un nuevo archivo.'
                    ],
                  },
                ],
              },
              {
                content_type: ContentType.ListItem,
                content: [
                  {
                    content_type: ContentType.Title3,
                    class: 'bold',
                    content: ['Confirma la actualización del archivo: '],
                  },
                  {
                    content_type: ContentType.Paragraph,
                    content: [
                      'Confirma los cambios para reemplazar el archivo existente por la nueva versión seleccionada.'
                    ],
                  },
                  {
                    content_type: ContentType.Paragraph,
                    class: 'msg-alert',
                    content: [
                      '<strong>Nota:</strong> El archivo reemplazado no se puede recuperar; para evitar problemas, asegúrate de tener una copia en tu dispositivo antes de proceder',
                    ],
                  },
                ],
              },
            ],
          }
        ],
      },
      how_to_delete: {
        title: 'Eliminar un archivo',
        content_type: ContentType.Fragment,
        content: [
          {
            content_type: ContentType.Paragraph,
            content: [
              'Eliminar archivos en <strong>wicadu</strong> te permite deshacerte de documentos innecesarios en tus <em>pedidos</em>, manteniendo tus registros limpios. Esta acción asegura un espacio de trabajo ordenado para todos los tipos de órdenes, evitando confusiones con clientes.',
              'Sigue los pasos a continuación para eliminar documentos: '
            ],
          },
          {
            content_type: ContentType.OrderedList,
            content: [
              {
                content_type: ContentType.ListItem,
                content: [
                  {
                    content_type: ContentType.Title3,
                    class: 'bold',
                    content: ['Selecciona el pedido en el módulo correspondiente: '],
                  },
                  {
                    content_type: ContentType.Paragraph,
                    content: [
                      'Dirígete a la funcionalidad de <em>Ventas</em> o <em>Compras</em> en el <strong>Asistente de wicadu</strong> y, en la sección de pedidos <em>Preparado</em> o <em>Entregado</em>, elige el pedido que contiene el archivo a eliminar.',
                    ],
                  },
                ],
              },
              {
                content_type: ContentType.ListItem,
                content: [
                  {
                    content_type: ContentType.Title3,
                    class: 'bold',
                    content: ['Inicia la eliminación del archivo: '],
                  },
                  {
                    content_type: ContentType.Paragraph,
                    content: [
                      'En la sección <em>Documentos</em> del pedido, haz clic en <strong>Editar</strong>, luego en el botón rojo al costado izquierdo del archivo que deseas eliminar y confirma con el botón al costado derecho.'
                    ],
                  },

                ],
              },
              {
                content_type: ContentType.ListItem,
                content: [
                  {
                    content_type: ContentType.Title3,
                    class: 'bold',
                    content: ['Confirma la anulación:'],
                  },
                  {
                    content_type: ContentType.Paragraph,
                    content: [
                      'Confirma la acción para eliminar el archivo. Los cambios se reflejarán de inmediato en el pedido',
                    ],
                  },
                  {
                    content_type: ContentType.Paragraph,
                    class: 'msg-alert',
                    content: [
                      '<strong>Nota:</strong> El archivo eliminado no se puede recuperar; para evitar problemas, asegúrate de tener una copia en tu dispositivo antes de proceder',
                    ],
                  },
                ],
              },
            ],
          }
        ],
      },
      next_steps: {
        title: 'Siguientes pasos',
        content_type: ContentType.Fragment,
        content: [
          {
            content_type: ContentType.Paragraph,
            content: [
              'Tras <strong>gestionar los archivos</strong> en el pedido, considera estas acciones como tu siguientes pasos a seguir:'
            ],
          },
          {
            content_type: ContentType.UnorderedList,
            content: [
              {
                content_type: ContentType.ListItem,
                content: [
                  {
                    content_type: ContentType.Title3,
                    class: 'bold',
                    content: ['Agregar Pagos: '],
                  },
                  {
                    content_type: ContentType.Paragraph,
                    content: [
                      `Registra los pagos realizados por el cliente en
                      <a href=${getUrlByPageId(PagesId.ManagingPaymentsForOrders, Languages.Spanish)} title="Guía para agregar pagos a un pedido">Cómo agregar pagos a un pedido</a>.`,
                    ],
                  },
                ],
              },
              {
                content_type: ContentType.ListItem,
                content: [
                  {
                    content_type: ContentType.Title3,
                    class: 'bold',
                    content: ['Guarda copias de seguridad: '],
                  },
                  {
                    content_type: ContentType.Paragraph,
                    content: [
                      // ARTICLE IDEA
                      // 'Descarga tus <em>documentos</em> importantes con la <a href="http://help.localhost/es/orders/download-docs" title="Guía para descargar documentos en wicadu">Guía para Descargar Documentos</a> para proteger tu información.'
                      'Descarga tus <em>documentos</em> importantes para proteger tu información.'
                    ],
                  },
                ],
              },
            ],
          },
        ],
      },
      common_issues: {
        title: 'Problemas Comunes',
        content_type: ContentType.UnorderedList,
        content: [
          {
            content_type: ContentType.Paragraph,
            content: [
              `<strong>Error de "Archivo demasiado grande"</strong>: Asegúrate de que tu archivo sea menor a 2MB. Si es muy grande, compruébalo y usa un editor de imágenes o un compresor de PDF para reducirlo.`
            ],
          },
          {
            content_type: ContentType.Paragraph,
            content: [
              '<strong>Tipo de archivo no admitido</strong>: Solo se permiten archivos <em>.jpg</em>, <em>.jpeg</em>, <em>.png</em>, <em>.svg</em>, <em>.webp</em> y <em>.pdf</em>; convierte tu archivo si no cumple con estos formatos.'
            ],
          },
          {
            content_type: ContentType.Paragraph,
            content: [
              `<strong>La carga se atasca</strong>: Actualiza la página e inténtalo de nuevo. Verifica tu conexión a internet si el problema persiste.`
            ],
          },
          {
            content_type: ContentType.Paragraph,
            content: [
              `<strong>No encuentro la sección de documentos</strong>: Asegúrate de estar en la vista detallada de un pedido específico. Si la sección no aparece, contacta al equipo de
              <a href=${getUrlByPageId(PagesId.ContactUs, Languages.Spanish)} title="soporte de wicadu">Soporte de wicadu</a> para asistencia.`
            ],
          },
        ],
      },
    }
  },
  en: {
    seo_title: 'Uploading and managing files',
    seo_description: 'Learn how to attach, update, or delete documents like invoices or warranties in wicadu orders. Follow this step-by-step guide to streamline your store management with the wicadu Assistant',
    seo_keywords: 'attach documents wicadu, update files wicadu, remove files wicadu, wicadu store management, how to attach documents in wicadu, steps to update documents, how to delete files in wicadu, upload documents in wicadu, manage documents',
    page_id: PagesId.ManagingDocsForOrders,
    breadcrumbs: {
      home: 'Home',
      orders_home: 'Orders',
      how_to_manage_docs: 'Uploading and managing files',
    },
    article: {
      title: 'Uploading and managing files',
      intended: [
        `<strong>Store Owners</strong> or <strong>Admins</strong> with full access to manage <em>orders</em> and <em>documents</em> in the <a href=${process.env.STORES_WEBAPP_HOST} title="wicadu Assistant">wicadu Assistant</a>.`,
        '<strong>Users</strong> with permissions to upload or modify <em>attachments</em> in orders.'
      ],
      about: {
        title: 'About managing files',
        content_type: ContentType.Paragraph,
        content: [
          'Attaching files to <em>orders</em> in <strong>wicadu</strong> allows you to centralize key files, such as <em>invoices</em>, <em>product specifications</em>, <em>warranties</em>, or <em>identification documents</em>, directly within the <strong>wicadu Assistant</strong> for efficient order management. This feature simplifies <em>customer communication</em>, keeping all order-related information organized and accessible.'
        ],
      },
      before_begin: {
        title: 'Before you begin',
        content_type: ContentType.Fragment,
        content: [
          {
            content_type: ContentType.Paragraph,
            content: [
              'To attach or manage files in <strong>wicadu</strong> orders, ensure the following:'
            ],
          },
          {
            content_type: ContentType.UnorderedList,
            content: [
              {
                content_type: ContentType.Paragraph,
                content: [
                  'You have access to <em>Sales</em> or <em>Purchases</em>',
                ],
              },
              {
                content_type: ContentType.Paragraph,
                content: [
                  'Files are under 2MB in size.'
                ],
              },
              {
                content_type: ContentType.Paragraph,
                content: [
                  'Files are in supported formats: <em>.jpg</em>, <em>.jpeg</em>, <em>.png</em>, <em>.svg</em>, <em>.webp</em>, or <em>.pdf</em>.'
                ],
              },
              {
                content_type: ContentType.Paragraph,
                content: [
                  'There is no limit to the number of files you can attach to an order.'
                ],
              },
            ],
          },
          // ARTICLE IDEA
          // {
          //   content_type: ContentType.Paragraph,
          //   class: 'msg-alert',
          //   content: [
          //     `Consulta la <a href="http://help.localhost/es/orders/document-formats" title="Guía de formatos de archivos admitidos en wicadu">Guía de formatos de archivos admitidos</a> para más detalles sobre cómo gestionar <em>adjuntos</em> en todos los tipos de pedidos.`
          //   ],
          // },
        ],
      },
      how_to_add: {
        title: 'Upload a file',
        content_type: ContentType.Fragment,
        content: [
          {
            content_type: ContentType.Paragraph,
            content: [
              'Adding files to your <em>orders</em> in <strong>wicadu</strong> helps you keep all important information in one place — from <em>invoices</em> to <em>warranties</em> or receipts. This improves traceability, reduces errors, and streamlines daily management.',
              'Follow the steps below to upload a file: '
            ],
          },
          {
            content_type: ContentType.OrderedList,
            content: [
              {
                content_type: ContentType.ListItem,
                content: [
                  {
                    content_type: ContentType.Title3,
                    class: 'bold',
                    content: ['Select the order in the relevant module: '],
                  },
                  {
                    content_type: ContentType.Paragraph,
                    content: [
                      'Navigate to the <em>Sales</em> or <em>Purchases</em> functionality in the <strong>wicadu Assistant</strong>, depending on the order type, and select the order from the <em>Prepared</em> or <em>Delivered</em> sections',
                    ],
                  },
                ],
              },
              {
                content_type: ContentType.ListItem,
                content: [
                  {
                    content_type: ContentType.Title3,
                    class: 'bold',
                    content: ['Choose the upload method: '],
                  },
                  {
                    content_type: ContentType.Paragraph,
                    content: [
                      'In the <em>Attachments</em> section, select one of these options: '
                    ],
                  },
                  {
                    content_type: ContentType.UnorderedList,
                    content: [
                      {
                        content_type: ContentType.Paragraph,
                        content: [
                          '<b>Quick access</b>: Click <strong>Upload File</strong> to open your system window, select the file, and confirm. The name field will auto-fill but can be edited.'
                        ],
                      },
                      {
                        content_type: ContentType.Paragraph,
                        content: [
                          '<b>Full form</b>: Click <strong>Edit</strong>, fill in the name and file fields in the expanded form, and confirm.',
                        ],
                      },
                    ],
                  },
                ],
              },
              {
                content_type: ContentType.ListItem,
                content: [
                  {
                    content_type: ContentType.Title3,
                    class: 'bold',
                    content: ['Confirm the file upload: '],
                  },
                  {
                    content_type: ContentType.Paragraph,
                    content: [
                      'Click <em>Save</em> to upload the file. Files will appear in a list once uploaded.'
                    ],
                  },
                ],
              },

            ],
          }
        ]
      },
      how_to_update: {
        title: 'Update an File',
        content_type: ContentType.Fragment,
        content: [
          {
            content_type: ContentType.Paragraph,
            content: [
              "Updating files in <strong>wicadu</strong> ensures that you always have accurate and up-to-date information for every order. This feature not only builds customer trust but also streamlines order management, improves internal collaboration, and boosts your store's operational efficiency.",
              'Follow the steps below to update a file:'
            ],
          },
          {
            content_type: ContentType.OrderedList,
            content: [
              {
                content_type: ContentType.ListItem,
                content: [
                  {
                    content_type: ContentType.Title3,
                    class: 'bold',
                    content: ['Select the order in the relevant module: '],
                  },
                  {
                    content_type: ContentType.Paragraph,
                    content: [
                      'Navigate to the <em>Sales</em> or <em>Purchases</em> functionality in the <strong>wicadu Assistant</strong>, depending on the order type, and select the order from the <em>Prepared</em> or <em>Delivered</em> sections',
                    ],
                  },
                ],
              },
              {
                content_type: ContentType.ListItem,
                content: [
                  {
                    content_type: ContentType.Title3,
                    class: 'bold',
                    content: ['Select the file to update: '],
                  },
                  {
                    content_type: ContentType.Paragraph,
                    content: [
                      'In the <em>Attachments</em> section of the order, click <strong>Edit</strong> to activate <em>edit mode</em> and manage the attached files.'
                    ],
                  },
                ],
              },
              {
                content_type: ContentType.ListItem,
                content: [
                  {
                    content_type: ContentType.Title3,
                    class: 'bold',
                    content: ['Enable the update form: '],
                  },
                  {
                    content_type: ContentType.Paragraph,
                    content: [
                      'Click the pencil icon to the left of the file you want to update to open the form, where you can change the name or upload a new file.',
                    ],
                  },
                ],
              },
              {
                content_type: ContentType.ListItem,
                content: [
                  {
                    content_type: ContentType.Title3,
                    class: 'bold',
                    content: ['Confirm the file update: '],
                  },
                  {
                    content_type: ContentType.Paragraph,
                    content: [
                      'Confirm the changes to replace the existing file with the newly selected version.'
                    ],
                  },
                  {
                    content_type: ContentType.Paragraph,
                    class: 'msg-alert',
                    content: [
                      '<strong>Note</strong>: The replaced file cannot be recovered; to avoid issues, keep a copy on your device before proceeding.',
                    ],
                  },
                ],
              },
            ],
          }
        ],
      },
      how_to_delete: {
        title: 'Delete a File',
        content_type: ContentType.Fragment,
        content: [
          {
            content_type: ContentType.Paragraph,
            content: [
              'Deleting files in <strong>wicadu</strong> allows you to remove unnecessary files from your <em>orders</em>, keeping your records clean. This action ensures an organized workspace for all types of orders, helping to prevent confusion with customers.',
              'Follow the steps below to delete a file:'
            ],
          },
          {
            content_type: ContentType.OrderedList,
            content: [
              {
                content_type: ContentType.ListItem,
                content: [
                  {
                    content_type: ContentType.Title3,
                    class: 'bold',
                    content: ['Select the order in the relevant module: '],
                  },
                  {
                    content_type: ContentType.Paragraph,
                    content: [
                      'Navigate to the <em>Sales</em> or <em>Purchases</em> functionality in the <strong>wicadu Assistant</strong>, depending on the order type, and select the order from the <em>Prepared</em> or <em>Delivered</em> sections',
                    ],
                  },
                ],
              },
              {
                content_type: ContentType.ListItem,
                content: [
                  {
                    content_type: ContentType.Title3,
                    class: 'bold',
                    content: ['Start the file deletion: '],
                  },
                  {
                    content_type: ContentType.Paragraph,
                    content: [
                      'In the <em>Attachments</em> section of the order, click <strong>Edit</strong>, then click the red button on the left side of the file you want to delete.'
                    ],
                  },

                ],
              },
              {
                content_type: ContentType.ListItem,
                content: [
                  {
                    content_type: ContentType.Title3,
                    class: 'bold',
                    content: ['Confirm the removal:'],
                  },
                  {
                    content_type: ContentType.Paragraph,
                    content: [
                      'Confirm by clicking the button on the right to proceed with the permanent deletion of the file. The changes will be reflected immediately in the order.'
                    ],
                  },
                  {
                    content_type: ContentType.Paragraph,
                    class: 'msg-alert',
                    content: [
                      '<strong>Note</strong>: The deleted file cannot be recovered; to avoid issues, keep a copy on your device before proceeding.',
                    ],
                  },
                ],
              },
            ],
          }
        ],
      },
      next_steps: {
        title: 'Next Steps',
        content_type: ContentType.Fragment,
        content: [
          {
            content_type: ContentType.Paragraph,
            content: [
              'After managing the files in your <em>orders</em>, explore these actions as your next steps:'
            ],
          },
          {
            content_type: ContentType.UnorderedList,
            content: [
              {
                content_type: ContentType.ListItem,
                content: [
                  {
                    content_type: ContentType.Title3,
                    class: 'bold',
                    content: ['Add Payments: '],
                  },
                  {
                    content_type: ContentType.Paragraph,
                    content: [
                      `Record customer payments following this guide:
                      <a href=${getUrlByPageId(PagesId.ManagingPaymentsForOrders, Languages.English)} title="How to Add Payments to an Order Guide">How to Add Payments to an Order</a>.`,
                    ],
                  },
                ],
              },
              {
                content_type: ContentType.ListItem,
                content: [
                  {
                    content_type: ContentType.Title3,
                    class: 'bold',
                    content: ['Save backups: '],
                  },
                  {
                    content_type: ContentType.Paragraph,
                    content: [
                      // ARTICLE IDEA
                      // 'Download your important <em>documents</em> using the <a href="http://help.localhost/en/orders/download-docs" title="Guide to Download Documents in wicadu">Guide to Download Documents</a> to protect your information.'
                      'Download your important <em>files</em> to protect your information.'
                    ],
                  },
                ],
              },
            ],
          },
        ],
      },
      common_issues: {
        title: 'Common Issues',
        content_type: ContentType.UnorderedList,
        content: [
          {
            content_type: ContentType.Paragraph,
            content: [
              `<strong>"File too large" error</strong>: Ensure your file is under 2MB. Try compressing or resizing if it's too big.`
            ],
          },
          {
            content_type: ContentType.Paragraph,
            content: [
              '<strong>Unsupported file type</strong>: Only <em>.jpg</em>, <em>.jpeg</em>, <em>.png</em>, <em>.svg</em>, <em>.webp</em>, and <em>.pdf</em> files are allowed.'
            ],
          },
          {
            content_type: ContentType.Paragraph,
            content: [
              `<strong>Upload gets stuck</strong>: Refresh the page and try again. Check your internet connection.`
            ],
          },
          {
            content_type: ContentType.Paragraph,
            content: [
              `<strong>Can't find the document section</strong>: Make sure you're in the detailed view of a specific order. If the section is missing, contact
              <a href=${getUrlByPageId(PagesId.ContactUs, Languages.English)} title="wicadu Support">wicadu Support</a> team for assistance.`
            ],
          },
        ],
      },
    }
  },
  fr: {
    seo_title: 'Gérer les documents des commandes',
    seo_description:
      'Découvrez comment ajouter, mettre à jour et supprimer des documents liés à vos commandes dans wicadu.',
    seo_keywords:
      'documents commande, pièces jointes commande, facture, reçu, preuve de livraison, wicadu commandes',
    page_id: PagesId.ManagingDocsForOrders,
    breadcrumbs: {
      home: 'Accueil',
      orders_home: 'Commandes',
      how_to_manage_docs: 'Téléverser et gérer les fichiers',
    },
    article: {
      title: 'Téléverser et gérer les fichiers',
      intended: [
        `<strong>Propriétaires</strong>, <strong>administrateurs</strong> et équipes qui gèrent les <em>documents de commande</em> dans l’<a href=${process.env.STORES_WEBAPP_HOST}>assistant wicadu</a>.`,
        '<strong>Vendeurs</strong> ou équipes opérationnelles chargés d’ajouter des justificatifs à une commande.',
      ],
      about: {
        title: 'À propos des documents de commande',
        content_type: ContentType.Paragraph,
        content: [
          'Les <strong>documents de commande</strong> vous permettent de centraliser les informations importantes liées à chaque <strong>vente</strong> ou <strong>achat</strong> dans <strong>wicadu</strong>. Ils facilitent le suivi quotidien, améliorent la traçabilité et permettent à votre équipe de retrouver rapidement les pièces utiles pendant la gestion d’une commande.',
          'Selon votre processus, vous pouvez joindre des <strong>factures</strong>, <strong>reçus</strong>, <strong>bons</strong>, justificatifs de <strong>paiement</strong>, garanties, preuves d’expédition ou preuves de livraison. Une documentation bien organisée améliore la gestion opérationnelle et réduit les erreurs lors du suivi des <strong>commandes</strong>.',
        ],
      },
      before_begin: {
        title: 'Avant de commencer',
        content_type: ContentType.Fragment,
        content: [
          {
            content_type: ContentType.Paragraph,
            content: ['Pour joindre ou gérer des fichiers dans les commandes <strong>wicadu</strong>, assurez-vous des éléments suivants :'],
          },
          {
            content_type: ContentType.UnorderedList,
            content: [
              { content_type: ContentType.Paragraph, content: ['Vous avez accès aux modules <em>Ventes</em> ou <em>Achats</em>.'] },
              { content_type: ContentType.Paragraph, content: ['Les fichiers ont une taille inférieure à 2 Mo.'] },
              { content_type: ContentType.Paragraph, content: ['Les fichiers sont dans un format pris en charge : <em>.jpg</em>, <em>.jpeg</em>, <em>.png</em>, <em>.svg</em>, <em>.webp</em> ou <em>.pdf</em>.'] },
              { content_type: ContentType.Paragraph, content: ['Il n’existe pas de limite quant au nombre de fichiers que vous pouvez joindre à une commande.'] },
            ],
          },
        ],
      },
      how_to_add: {
        title: 'Comment ajouter un document',
        content_type: ContentType.Fragment,
        content: [
          {
            content_type: ContentType.Paragraph,
            content: [
              'Ajouter des fichiers à vos <em>commandes</em> dans <strong>wicadu</strong> vous aide à conserver toutes les informations importantes au même endroit, qu’il s’agisse de <em>factures</em>, de <em>garanties</em> ou de justificatifs. Cela améliore la traçabilité, réduit les erreurs et facilite la gestion quotidienne.',
              'Suivez les étapes ci-dessous pour téléverser un fichier : ',
            ],
          },
          {
            content_type: ContentType.OrderedList,
            content: [
              {
                content_type: ContentType.ListItem,
                content: [
                  { content_type: ContentType.Title3, class: 'bold', content: ['Sélectionner la commande dans le module concerné : '] },
                  { content_type: ContentType.Paragraph, content: ['Accédez à la fonctionnalité <em>Ventes</em> ou <em>Achats</em> dans l’<strong>assistant wicadu</strong>, selon le type de commande, puis sélectionnez la commande depuis les sections <em>Préparé</em> ou <em>Livré</em>.'] },
                ],
              },
              {
                content_type: ContentType.ListItem,
                content: [
                  { content_type: ContentType.Title3, class: 'bold', content: ['Choisir la méthode de téléversement : '] },
                  { content_type: ContentType.Paragraph, content: ['Dans la section <em>Documents</em>, choisissez l’une de ces options :'] },
                  {
                    content_type: ContentType.UnorderedList,
                    content: [
                      {
                        content_type: ContentType.Paragraph,
                        content: [
                          '<b>Accès rapide</b> : cliquez sur <strong>Téléverser un fichier</strong> pour ouvrir la fenêtre de votre système, sélectionnez le fichier puis confirmez. Le champ du nom se remplira automatiquement, mais vous pourrez le modifier.',
                        ],
                      },
                      {
                        content_type: ContentType.Paragraph,
                        content: [
                          '<b>Formulaire complet</b> : cliquez sur <strong>Modifier</strong>, remplissez les champs du nom et du fichier dans le formulaire affiché, puis confirmez.',
                        ],
                      },
                    ],
                  },
                ],
              },
              {
                content_type: ContentType.ListItem,
                content: [
                  { content_type: ContentType.Title3, class: 'bold', content: ['Confirmer le téléversement du fichier : '] },
                  { content_type: ContentType.Paragraph, content: ['Cliquez sur <em>Enregistrer</em> pour téléverser le fichier. Les fichiers apparaîtront dans une liste une fois ajoutés.'] },
                ],
              },
            ],
          },
        ],
      },
      how_to_update: {
        title: 'Comment mettre à jour un document',
        content_type: ContentType.Fragment,
        content: [
          {
            content_type: ContentType.Paragraph,
            content: [
              'Mettre à jour des documents dans <strong>wicadu</strong> garantit que vous disposez toujours d’informations exactes et à jour pour chaque commande. Cette fonctionnalité renforce la confiance de vos clients, simplifie la gestion des commandes et améliore l’efficacité opérationnelle de votre boutique.',
              'Suivez les étapes ci-dessous pour mettre à jour un fichier : ',
            ],
          },
          {
            content_type: ContentType.OrderedList,
            content: [
              {
                content_type: ContentType.ListItem,
                content: [
                  { content_type: ContentType.Title3, class: 'bold', content: ['Sélectionner la commande dans le module concerné : '] },
                  { content_type: ContentType.Paragraph, content: ['Accédez à la fonctionnalité <em>Ventes</em> ou <em>Achats</em> dans l’<strong>assistant wicadu</strong>, selon le type de commande, puis sélectionnez la commande depuis les sections <em>Préparé</em> ou <em>Livré</em>.'] },
                ],
              },
              {
                content_type: ContentType.ListItem,
                content: [
                  { content_type: ContentType.Title3, class: 'bold', content: ['Sélectionner le fichier à mettre à jour : '] },
                  { content_type: ContentType.Paragraph, content: ['Dans la section <em>Documents</em> de la commande, cliquez sur <strong>Modifier</strong> pour activer le <em>mode édition</em> et gérer les fichiers joints disponibles.'] },
                ],
              },
              {
                content_type: ContentType.ListItem,
                content: [
                  { content_type: ContentType.Title3, class: 'bold', content: ['Activer le formulaire de mise à jour : '] },
                  { content_type: ContentType.Paragraph, content: ['Cliquez sur l’icône en forme de crayon à gauche du fichier que vous souhaitez mettre à jour pour ouvrir le formulaire, où vous pourrez modifier le nom ou téléverser un nouveau fichier.'] },
                ],
              },
              {
                content_type: ContentType.ListItem,
                content: [
                  { content_type: ContentType.Title3, class: 'bold', content: ['Confirmer la mise à jour du fichier : '] },
                  { content_type: ContentType.Paragraph, content: ['Confirmez les changements afin de remplacer le fichier existant par la nouvelle version sélectionnée.'] },
                  {
                    content_type: ContentType.Paragraph,
                    class: 'msg-alert',
                    content: [
                      '<strong>Remarque :</strong> Le fichier remplacé ne peut pas être récupéré. Pour éviter tout problème, conservez une copie sur votre appareil avant de continuer.',
                    ],
                  },
                ],
              },
            ],
          },
        ],
      },
      how_to_delete: {
        title: 'Comment supprimer un document',
        content_type: ContentType.Fragment,
        content: [
          {
            content_type: ContentType.Paragraph,
            content: [
              'Supprimer des fichiers dans <strong>wicadu</strong> vous permet de retirer les documents inutiles de vos <em>commandes</em> et de garder vos enregistrements propres. Cette action contribue à maintenir un espace de travail organisé pour tous les types de commandes et à éviter les confusions avec les clients.',
              'Suivez les étapes ci-dessous pour supprimer un fichier : ',
            ],
          },
          {
            content_type: ContentType.OrderedList,
            content: [
              {
                content_type: ContentType.ListItem,
                content: [
                  { content_type: ContentType.Title3, class: 'bold', content: ['Sélectionner la commande dans le module concerné : '] },
                  { content_type: ContentType.Paragraph, content: ['Accédez à la fonctionnalité <em>Ventes</em> ou <em>Achats</em> dans l’<strong>assistant wicadu</strong> et, dans la section des commandes <em>Préparé</em> ou <em>Livré</em>, choisissez la commande qui contient le fichier à supprimer.'] },
                ],
              },
              {
                content_type: ContentType.ListItem,
                content: [
                  { content_type: ContentType.Title3, class: 'bold', content: ['Commencer la suppression du fichier : '] },
                  { content_type: ContentType.Paragraph, content: ['Dans la section <em>Documents</em> de la commande, cliquez sur <strong>Modifier</strong>, puis sur le bouton rouge situé à gauche du fichier que vous souhaitez supprimer, et confirmez avec le bouton situé à droite.'] },
                ],
              },
              {
                content_type: ContentType.ListItem,
                content: [
                  { content_type: ContentType.Title3, class: 'bold', content: ['Confirmer la suppression : '] },
                  { content_type: ContentType.Paragraph, content: ['Confirmez l’action pour supprimer le fichier. Les changements seront immédiatement reflétés dans la commande.'] },
                  {
                    content_type: ContentType.Paragraph,
                    class: 'msg-alert',
                    content: [
                      '<strong>Remarque :</strong> Le fichier supprimé ne peut pas être récupéré. Pour éviter tout problème, conservez une copie sur votre appareil avant de continuer.',
                    ],
                  },
                ],
              },
            ],
          },
        ],
      },
      next_steps: {
        title: 'Étapes suivantes',
        content_type: ContentType.Fragment,
        content: [
          { content_type: ContentType.Paragraph, content: ['Après avoir <strong>géré les fichiers</strong> dans vos <em>commandes</em>, vous pouvez envisager les actions suivantes :'] },
          {
            content_type: ContentType.UnorderedList,
            content: [
              {
                content_type: ContentType.ListItem,
                content: [
                  { content_type: ContentType.Title3, class: 'bold', content: ['Ajouter des paiements : '] },
                  { content_type: ContentType.Paragraph, content: [`Enregistrez les paiements réalisés par le client dans
                      <a href=${getUrlByPageId(PagesId.ManagingPaymentsForOrders, Languages.French)} title="Guide pour ajouter des paiements à une commande">Comment ajouter des paiements à une commande</a>.`] },
                ],
              },
              {
                content_type: ContentType.ListItem,
                content: [
                  { content_type: ContentType.Title3, class: 'bold', content: ['Conserver des copies de sauvegarde : '] },
                  { content_type: ContentType.Paragraph, content: ['Téléchargez vos <em>documents</em> importants afin de protéger vos informations.'] },
                ],
              },
            ],
          },
        ],
      },
      common_issues: {
        title: 'Problèmes fréquents',
        content_type: ContentType.UnorderedList,
        content: [
          { content_type: ContentType.Paragraph, content: ['<strong>Erreur "Fichier trop volumineux"</strong> : assurez-vous que votre fichier fait moins de 2 Mo. S’il est trop grand, vérifiez-le et utilisez un éditeur d’images ou un compresseur PDF pour réduire sa taille.'] },
          { content_type: ContentType.Paragraph, content: ['<strong>Type de fichier non pris en charge</strong> : seuls les fichiers <em>.jpg</em>, <em>.jpeg</em>, <em>.png</em>, <em>.svg</em>, <em>.webp</em> et <em>.pdf</em> sont autorisés ; convertissez votre fichier s’il ne correspond pas à ces formats.'] },
          { content_type: ContentType.Paragraph, content: ['<strong>Le téléversement reste bloqué</strong> : actualisez la page et réessayez. Vérifiez votre connexion internet si le problème persiste.'] },
          { content_type: ContentType.Paragraph, content: [`<strong>Je ne trouve pas la section des documents</strong> : assurez-vous d’être dans la vue détaillée d’une commande précise. Si la section n’apparaît pas, contactez l’équipe de
              <a href=${getUrlByPageId(PagesId.ContactUs, Languages.French)} title="support wicadu">support wicadu</a> pour obtenir de l’aide.`] },
        ],
      },
    },
  },
  pt: {},
}


export default globalTranslations
